import { intersection, isNil, cloneDeep } from 'lodash';
import NP from 'number-precision';
import dayjs from 'dayjs';
import { RuleTypeEnum } from '../constants';

const flatTree = (list) => {
  return list.reduce((ls, item) => {
    const { children, ...res } = item;
    if (children?.length) {
      return ls.concat(res, flatTree(children));
    } else {
      return ls.concat(res);
    }
  }, []);
};

export const TreeToMap = (tree) => {
  const list = flatTree(tree);
  const ret = {}; 
  list?.forEach(element => {
    ret[element.code] = element.name;
  }); 
  return ret;
};

export const validateCol = (dataIndex, form) => {
  const fieldsValue = form.getFieldsValue();
  const fieldsName = Object.keys(fieldsValue);
  const validateNames = fieldsName.filter(f => f.includes(dataIndex));
  form.validateFields(validateNames);
};

export const validateCascader = (placeCode, callback, record , data) => {
  const rowList = placeCode?.map(item => item?.slice(-1)[0]);
  // 找出除去自身的其他数据作为比较
  const compareList = [];
  data?.forEach(item => {
    if (item.uuid !== record.uuid) {
      item.destination?.forEach(d => {
        compareList.push(d.placeCode);
      });
    }
  });
  // 交集的列表
  const intersectionPlaceCode = intersection(compareList, rowList);
  if (!intersectionPlaceCode.length) {
    return callback();
  } else {
    return callback(`${intersectionPlaceCode.length}个地区重复`);
  }
};

// 校验本列的数据是否冲突
export const validateTableColumn = (dataIndex, form) => {
  const fieldsValue = form.getFieldsValue();
  const fieldsName = Object.keys(fieldsValue);
  const validateNames = fieldsName.filter(f => f.includes(dataIndex));

  form.validateFields(validateNames);
};

// 普通规则: 目的地冲突校验
export const validateDestination = (placeCode, callback, record, data) => {
  const rowList = placeCode?.map(item => item?.slice?.(-1)?.[0]);

  const compareList = [];
  data?.forEach(item => {
    if (item.uuid !== record.uuid) {
      compareList.push(...item.destination);
    }
  });

  // 比较placeCode
  const compareCodeList = compareList?.map(i => i.placeCode);
  // 交集的列表
  const intersectionPlaceCode = intersection(compareCodeList, rowList);
  /**
   * destination: { 
   *  pathCodeChain: "330000000000,330100000000,330102000000", 
   *  placeCode: "330102000000", 
   *  placeName: "浙江省,杭州市,上城区"
   * }
   * placeName: '浙江省,杭州市,上城区' => 转成'上城区'
   * 2n^2
   */ 
  const intersectionPlaceName = record.destination.filter(i => intersectionPlaceCode.includes(i.placeCode))?.map(i => i.placeName?.split(',')?.slice(-1));

  if (!intersectionPlaceCode.length) {
    return callback();
  } else {
    return callback(`${intersectionPlaceCode.length}个地区重复: ${intersectionPlaceName?.join('、')}`);
  }
};

// 伙食常驻地校验
export const validateFoodDestination = (callback, record, data, selfIdx) => {
  // 排列组合
  const findList = (residence, destination) => {
    const list = [];
    destination?.forEach(j => {
      list.push(`${residence ? residence?.split?.(',')?.[2] : 'empty'}-${j.placeCode}`);
    });

    return list;
  };
  const rowList = findList(record.residence, record.destination);

  const intersectionCodeList = [];
  data?.forEach((item, index) => {
    if(item.destination && (index !== selfIdx)) {
      const resList = findList(item.residence, item.destination);
      const intersectionPlaceCode = intersection(resList, rowList);
      if (intersectionPlaceCode.length) {
        intersectionCodeList.push(...intersectionPlaceCode)
      }
    }
  });

  if (!intersectionCodeList.length) {
    return callback();
  } else {
    try {
      const intersectionPlaceName = record.destination.filter(i => intersectionCodeList?.map(v => v.split('-')?.[1]).includes(i.placeCode))?.map(i => i.placeName?.split(',')?.slice(-1));
      return callback(`${intersectionPlaceName.length}个地区重复: ${intersectionPlaceName?.join('、')}`);
    } catch (error) {
      console.log('error', error)
      return callback();
    }
  }
};

// 特殊规则-出发地/目的地校验
export const validatePlaceRange = (callback, record, needTipName, selfIdx, data) => {
  if (!record.departure || !record.destination) return callback();

  // 排列组合
  const findList = (departure, destination) => {
    const list = [];
    departure?.forEach(i => {
      destination?.forEach(j => {
        list.push(`${i.placeCode}-${j.placeCode}`);
      });
    });

    return list;
  };

  // 当前
  const rowList = findList(record.departure, record.destination);
  // 重复行的index
  const indexList = [];
  data?.forEach((item, index) => {
    if(item.departure && item.destination) {
      const resList = findList(item.departure, item.destination);
      const intersectionPlaceCode = intersection(resList, rowList);
      if (intersectionPlaceCode.length) {
        indexList.push(index);
      }
    }
  });

  if (indexList.length <= 1) {
    return callback();
  } else {
    const displayRow = indexList.filter(i => i !== selfIdx)?.map(j => j + 1);
    const desc = `与第${displayRow?.join('、')}行存在重复行程`;
    return  needTipName === 'departure' ? callback(desc) : callback();
  }
};


// 选择地区Cascader onChange 回调
export const handlePlaceChange = async (selected, itemName, form) => {
  const list = selected?.map(item => {
    const pathCodeChain = item?.map(i => i.value)?.join(',');
    const placeName = item?.map(i => i.name)?.join(',');

    const placeCode = !pathCodeChain?.split(',')?.length ? pathCodeChain :  pathCodeChain?.split(',')?.slice(-1)[0];

    return ({
      placeName,
      placeCode,
      pathCodeChain,
    });
  });

  form.setFieldsValue({
    [itemName]: list
  });

  validateTableColumn('destination', form);
  validateTableColumn('departure', form);
};

// 找到FormData中对应的uuid、FormItemName的值
const findInForm = (uuid, itemName, formData) => {
  const keys = Object.keys(formData);
  let value;
  keys?.forEach(key => {
    if(key.includes(uuid) && key.includes(itemName)) {
      value = formData[key];
    }
  });
  return value;
};

// 转换差旅伙食补贴和公杂DTO
export function transferTripFormToDTO (formData, dataSource, travelType, ruleType, DTO) {
  const newDTO = cloneDeep(DTO);
  dataSource?.forEach(item => {
    const { uuid, dateConditionDTOS } = item;

    const destinationList = formData[`destinationInfo_${uuid}`] || [];
    const departureList = formData[`departureInfo_${uuid}`] || [];
    const isUseInvoice = formData[`isUseInvoice_${uuid}`];
    const residenceList = formData[`residence_${uuid}`] || [];
    const residenceName = formData[`residenceName_${uuid}`] || [];

    item.destination = cloneDeep(destinationList);
    item.departure = cloneDeep(departureList);
    item.isUseInvoice = isUseInvoice;
    item.residence = residenceList?.join(',');
    item.residenceName = residenceName?.join(',');
    
    dateConditionDTOS?.forEach(ele => {
      const { uuid: uuidInner } = ele;
      /**
       * 差旅公杂用到sendCarAmount、notSendCarAmount
       * 差旅伙食用到amount
       */
      const amount = findInForm(uuidInner, 'amount', formData);
      const sendCarAmount = findInForm(uuidInner, 'sendCarAmount', formData);
      const notSendCarAmount = findInForm(uuidInner, 'notSendCarAmount', formData);
      /**
       * 差旅公杂新增补贴次数字段
       */
      const allowanceTimes = findInForm(uuidInner, 'allowanceTimes', formData);

      ele.amount = !isNil(amount) ? NP.times(Number(amount), 100) : undefined;
      ele.sendCarAmount = !isNil(sendCarAmount) ?  NP.times(Number(sendCarAmount), 100) : undefined;
      ele.notSendCarAmount = !isNil(notSendCarAmount) ? NP.times(Number(notSendCarAmount), 100) : undefined;
      ele.allowanceTimes = allowanceTimes;
    });
  });

  newDTO?.forEach(dto => {
    if (travelType === dto.travelType && ruleType === RuleTypeEnum.Normal) {
      dto.normalData = cloneDeep(dataSource);
    } else if (travelType === dto.travelType && ruleType === RuleTypeEnum.Special) {
      dto.specialData = cloneDeep(dataSource);
    }
  });

  return newDTO;
};


// 格式化区划树
export const formatTreeData = (data = []) => {
  return (data || [])?.map(i => ({
    label: i.name || i.typeName,
    value: i.code || i.typeCode,
    ...i,
    children: (i.children && i.children.length > 0) ? formatTreeData(i.children) : null,
  }));
};
// 转换培训模板DTO
export function transferTrainingFormToDTO (formData, dataSource) {
  const newData = [...dataSource];
  newData?.forEach(item => {
    const { levelValue, data } = item;
    data?.forEach(ele => {
      const { uuid } = ele;
      // NOTE: 如果formItem的name改了，这里也要改
      const amount = formData[`amount_${levelValue}_${uuid}`];
      ele.amount = !isNil(amount) ? NP.times(amount, 100) : undefined;
    });
    // }
  });

  return newData;
};

// 获取地址完整数据
// TODO 方法待优化
export function formatAreaData (tree, code) {
  let payload = {
    placeName: '',
    placeCode: '',
    placeLevel: code?.length || 0,
    pathCodeChain: code?.join(','),
  };
  let currentData;
  const  [ pr = undefined, city = undefined, county = undefined] = code;
  const currentPr = tree.find(item => item.value === pr );
  if(currentPr?.children) {
    const currentCity = (currentPr.children || []).find(item => item.value === city );
    if(currentCity?.children) {
      const currentConunty = (currentCity.children || []).find(item => item.value === county );
      currentData = currentConunty;
    }else{
      currentData = currentCity;
    }
    
  }else{
    currentData = currentPr;
  }
  const { name = undefined, value = undefined } = currentData || {};
  payload = {
    ...payload,
    placeName: name,
    placeCode: value
  };
  return payload;
}

export const transferCentToYuan = (cent) => {
  if(isNaN(cent) || isNil(cent)) { return undefined }
  return NP.round(NP.divide(cent, 100), 2);  
};

export const transferYuanToCent = (yuan) => {
  if(isNaN(yuan) || isNil(yuan)) { return undefined }
  return NP.times(yuan, 100);  
};

export const formatPlaceDisplay = (placeName = '') => {
  return placeName?.split(',')?.join('/') || [];
};

/**
 * 加班费控转换DTO
 */
export const transferOvertimeFormToDTO = (formData, dataSource) => {
  const newData = [...dataSource];
  newData?.forEach(item => {
    const amount = formData[`amount_${item.uuid}`];
    item.amount = !isNil(amount) ? NP.times(amount, 100) : undefined;

    item.dateType = formData[`dateType_${item.uuid}`];
    item.startTime = formData[`dayTime_${item.uuid}`]?.[0]?.valueOf();
    item.endTime = formData[`dayTime_${item.uuid}`]?.[1]?.valueOf();
    const { uuid, hoursAndTimesList } = item;
    hoursAndTimesList?.forEach(ele => {
      ele.hours = formData[`hours_${uuid}_${ele.uuid}`];
      ele.times = formData[`times_${uuid}_${ele.uuid}`];
    });
  });

  return newData;
};

/**
 * 加班时间端重复校验
 */
export const validateOvertimeDayTime = (value, callback, record, data) => {
  if (!value) return callback();
  const curStart = dayjs(value[0]);
  const curEnd = dayjs(value[1]);

  data?.forEach(i => {
    if (i.uuid !== record.uuid && i.dateType === record.dateType && i.startTime) {
      if (!(i.endTime < curStart || i.startTime > curEnd)) {
        return callback('存在冲突');
      }
    }
  });

  return callback();
};

/**
 * 加班时长校验
 */
export const validateOvertimeHour =  (value, callback, record) => {
  if (!value) return callback();
  const list = record.hoursAndTimesList?.map(i => i.hours);
  const newList = Array.from(new Set(list));

  if (list.length !== newList.length) {
    return callback('不能重复');
  }
  return callback();
};